<template>
  <b-container class="login-screen w-100 py-2 my-0" id="license">

    <div>
      <logos/>
      <h1 v-html="$config.productName"></h1>
      <hr/>
    </div>

    <b-card class="float-md-right login-card small-box m-2">
      <h2 class='login_title text-center'>Login</h2>
      <hr class="my-3"/>
      <overlay-spinner :loading="isBusy"/>
      <form v-on:submit.prevent="login">
        <b-form-input
          id="username"
          v-model="username"
          :disabled="isBusy"
          size="lg"
          required
          ref="username"
          type="text"
          class="mb-2"
          placeholder="Username"/>
        <b-form-input
          id="password"
          v-model="password"
          :disabled="isBusy"
          size="lg"
          required
          type="password"
          class="mb-2"
          placeholder="Password"/>
        <b-button
          :disabled="isBusy || !username || !password"
          variant="iiasa" size="lg" block type="submit"
          class="mb-2">
          Login
        </b-button>
        <b-button
          :disabled="isBusy"
          variant="outline-secondary" size="sm" class="mb-2"
          v-b-modal.registrationModal>
          Register
        </b-button>
        <b-button
          :disabled="isBusy"
          variant="outline-secondary" size="sm" class="mb-2 float-right"
          @click="resetPassword">
          Forgot password?
        </b-button>
        <template v-if="$config.login.guest">
          <hr/>
          <b-button
            :disabled="isBusy"
            variant="outline-secondary"
            block class="mb-2"
            @click="guestLogin">
            Guest login
          </b-button>
        </template>
        <template v-else>
          <br/>
        </template>
        <small class="float-right">
          <a href="http://www.iiasa.ac.at/web/terms-of-use.html" target="_blank">Terms of use</a>
        </small>
      </form>
      <b-alert show style="max-width: 300px;">
        The Scenario Explorer authentication system has recently been migrated.
        If you have trouble logging in please reset your password using the button above to reactivate your account.
      </b-alert>
    </b-card>

    <landing/>

    <b-modal
      id="registrationModal" title="Registration"
      header-bg-variant="light"
      header-text-variant="dark"
      body-bg-variant="light"
      body-text-variant="dark"
      footer-bg-variant="light"
      footer-text-variant="dark"
      :ok-only="!$config.login.register.self"
      ok-variant="iiasa"
      @ok="register">
      <p v-html="$config.login.register.message"></p>
    </b-modal>
  </b-container>
</template>

<script>
  import {mapActions} from 'vuex';
  import iamcLogo from '../assets/iamc_logo.png';
  import iiasaLogo from '../assets/iiasa_logo.png';

  export default {
    data: function () {
      return {
        iamcLogo,
        iiasaLogo,
        username: null,
        password: null,
        isBusy: false,
      };
    },
    components: {
      // Prefetch workspace file chunks so the workspace pages load faster.
      //
      // The webpackPrefetch comment will lead to an element likt this:
      // <link rel="prefetch" as="script" href="js/workspace-layout.79bbaa96.js">
      // Browsers prefetch this static asset after the DOMContentLoaded event.
      // Contrary to importing static assets in the 'mounted' method of a
      // component, prefetched assets are not parsed.

      // eslint-disable-next-line vue/no-unused-components
      WorkspaceSelector: () => import(/* webpackPrefetch: true */ './Workspace/Layout/WorkspaceSelector'),
      // eslint-disable-next-line vue/no-unused-components
      WorkspaceLayout: () => import(/* webpackPrefetch: true */ './Workspace/Layout/WorkspaceLayout'),
    },
    methods: {
      ...mapActions(['fetchUserDetails']),
      async login() {
        this.isBusy = true;
        try {
          await this.$auth.login(this.username, this.password);
          this.username = null;
          this.password = null;
          await this.$router.replace(this.$route.query.redirect || {name: 'index'});
        } catch (e) {
          // nextTick will not work as browser didn't render stuff yet (see diff of micro tasks and events)
          // this.$nextTick(() => this.$refs.username.$el.focus());
          setTimeout(() => this.$refs.username.$el.focus(), 0, this);
        }
        this.isBusy = false;
      },
      async guestLogin() {
        this.isBusy = true;
        try {
          await this.$auth.guestLogin();
          await this.$router.replace(this.$route.query.redirect || {name: 'index'});
        } catch (e) {
          this.$nextTick(() => this.$refs.username.$el.focus());
        }
        this.isBusy = false;
      },
      register() {
        if (this.$config.login.register.self) {
          window.open(this.registerAccountURL, '_blank');
        }
      },
      resetPassword() {
        window.open(this.resetPasswordURL, '_blank');
      },
    },
    computed: {
      resetPasswordURL() {
        if (this.$config.login.reset && this.$config.login.reset.url) {
          return `${this.$config.login.reset.url}?application=${this.$config.database}`;
        }
        return this.$config.authUrl.replace('/config/v1', '');
      },
      registerAccountURL() {
        if (this.$config.login.register && this.$config.login.register.url) {
          return `${this.$config.login.register.url}?application=${this.$config.database}`;
        }
        return this.$config.authUrl.replace('/config/v1', '');
      },
    },
    mounted() {
      this.$nextTick(() => this.$refs.username.$el.focus());
    },
  };
</script>

<style scoped>
  .login-screen {
    min-height: 100%;
  }

  .login-card {
    min-width: 320px;
  }

  @media only screen and (max-width: 500px) {
    .login-card {
      min-width: calc(100% - 4rem);
      margin-left: 0;
      margin-right: 0;
    }
  }

  .login_title {
    font-family: "myriad-pro", sans-serif;
    font-style: normal;
    font-weight: 100;
    color: rgba(164, 164, 164, 0.68);
    margin-top: 20px;
  }

  .shake:hover {
    animation: shake 0.82s cubic-bezier(.36, .07, .19, .97) both;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
  }

  @keyframes shake {
    10%, 90% {
      transform: translate3d(-1px, 0, 0);
    }

    20%, 80% {
      transform: translate3d(2px, 0, 0);
    }

    30%, 50%, 70% {
      transform: translate3d(-4px, 0, 0);
    }

    40%, 60% {
      transform: translate3d(4px, 0, 0);
    }
  }
</style>
